import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BEAUTY_PRODUCTS, beautyAppPath } from '../../../beauty-shared/config/saasModules';
import { useBeautyProduct } from '../../../beauty-shared/context/BeautyProductContext';
import MakeupAiLoader from '../../makeuphair/components/MakeupAiLoader';
import { resolveMakeupStyleStudioEntry } from '../../shared/lookSession';
import { fetchHairJobStatus, fetchHairResultObjectUrl } from '../../hair-styling/utils/hairStylingApi';
import {
  analyzeBeard,
  fetchBeardStyles,
  generateBeardLook,
  recommendBeardLooks,
} from '../utils/beardStylingApi';
import '../../hair-styling/pages/HairStylingStudio.css';
import './BeardStylingStudio.css';

const BEARD_TABS = [
  { id: 'recommended', label: 'Recommended' },
  { id: 'catalog', label: 'All styles' },
];

export default function BeardStylingStudio() {
  const { appPath, appBase, title: productTitle } = useBeautyProduct();
  const location = useLocation();
  const entry = resolveMakeupStyleStudioEntry(location.state);
  const sourceImage = entry.applyOriginal || location.state?.image || '';
  const originalCapture = entry.captureOriginal || sourceImage;

  const [tab, setTab] = useState('recommended');
  const [styles, setStyles] = useState([]);
  const [profile, setProfile] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [displayImage, setDisplayImage] = useState(sourceImage);
  const [busy, setBusy] = useState(false);
  const [loaderTitle, setLoaderTitle] = useState('');
  const [loaderMessage, setLoaderMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState('');

  const styleLink = appPath('studio');
  const colorBase = String(appBase || BEAUTY_PRODUCTS.hairColoring.appBase).replace(
    /hair-styling\/?$/,
    'hair-coloring',
  );
  const colorLink = beautyAppPath(colorBase, 'studio');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchBeardStyles();
        if (!cancelled) setStyles(data.styles || []);
      } catch {
        /* catalog optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!sourceImage) return undefined;
    let cancelled = false;
    (async () => {
      setBusy(true);
      setLoaderTitle('Analyzing beard');
      setLoaderMessage('Detecting mustache and beard coverage…');
      try {
        const analyzed = await analyzeBeard(sourceImage);
        const prof = analyzed.profile || null;
        if (cancelled) return;
        setProfile(prof);
        const rec = await recommendBeardLooks({ image: sourceImage, profile: prof });
        if (cancelled) return;
        setRecommendations(rec.recommendations || []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Analysis failed');
      } finally {
        if (!cancelled) {
          setBusy(false);
          setLoaderTitle('');
          setLoaderMessage('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceImage]);

  const catalogStyles = useMemo(() => styles, [styles]);

  const visibleStyles = useMemo(() => {
    if (tab === 'catalog') return catalogStyles;
    const ids = new Set((recommendations || []).map((r) => r.style_id));
    const recStyles = catalogStyles.filter((s) => ids.has(s.id));
    return recStyles.length ? recStyles : catalogStyles.slice(0, 6);
  }, [tab, catalogStyles, recommendations]);

  const pollJob = useCallback(async (jobId) => {
    for (let i = 0; i < 120; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const st = await fetchHairJobStatus(jobId);
      const status = st.job?.status || st.status;
      if (status === 'completed') {
        const url = await fetchHairResultObjectUrl(jobId);
        return url;
      }
      if (status === 'failed') {
        throw new Error(st.job?.error_message || 'Generation failed');
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 2000));
    }
    throw new Error('Timed out waiting for beard style');
  }, []);

  const onApply = useCallback(
    async (styleId) => {
      if (!sourceImage || !styleId) return;
      setError('');
      setSelectedId(styleId);
      setBusy(true);
      setLoaderTitle('Applying beard style');
      setLoaderMessage('Updating facial hair only…');
      try {
        const gen = await generateBeardLook({
          image: sourceImage,
          original_image: originalCapture || sourceImage,
          style_id: styleId,
        });
        const url = await pollJob(gen.job_id);
        setDisplayImage(url);
      } catch (e) {
        setError(e.message || 'Apply failed');
      } finally {
        setBusy(false);
        setLoaderTitle('');
        setLoaderMessage('');
      }
    },
    [sourceImage, originalCapture, pollJob],
  );

  return (
    <div className="hair-studio beard-studio">
      <header className="hair-studio__top">
        <h1 className="hair-studio__title">{productTitle ? `${productTitle} — Beard` : 'Beard styling'}</h1>
        <p className="hair-studio__lede">Analyze, recommend, and try beard &amp; mustache styles — scalp hair unchanged.</p>
        <nav className="beard-studio__module-nav" aria-label="Hair modules">
          <Link to={styleLink}>Hairstyle</Link>
          <Link to={colorLink}>Hair color</Link>
          <span className="is-active" aria-current="page">
            Beard
          </span>
        </nav>
        {profile ? (
          <p className="beard-studio__profile">
            {profile.has_beard ? 'Beard detected' : 'Little or no beard'}
            {profile.has_mustache ? ' · mustache' : ''}
            {profile.density ? ` · ${profile.density}` : ''}
          </p>
        ) : null}
      </header>

      <div className="hair-studio__stage beard-studio__layout">
        <div className="beard-studio__preview">
          {displayImage ? (
            <img src={displayImage} alt="Guest" className="hair-studio__preview" />
          ) : (
            <p className="hair-studio__empty">Capture a photo first</p>
          )}
        </div>

        <aside className="beard-studio__picker">
          <div className="studio-look-picker-subtabs" role="tablist">
            {BEARD_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={tab === t.id ? 'is-active' : ''}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          {error ? <p className="hair-studio__error">{error}</p> : null}
          <ul className="beard-studio__grid">
            {visibleStyles.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={selectedId === s.id ? 'is-selected' : ''}
                  disabled={busy || !sourceImage}
                  onClick={() => onApply(s.id)}
                >
                  <span className="beard-studio__name">{s.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </div>

      {busy ? (
        <MakeupAiLoader title={loaderTitle} message={loaderMessage} />
      ) : null}
    </div>
  );
}
